import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Container,
  FormFeedback,
} from "reactstrap"
import Dropzone from "react-dropzone"
import { useNavigate, useParams } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useSelector, useDispatch } from "react-redux"

import Breadcrumbs from "../../../components/Common/Breadcrumb"
import SuccessPopup from "../../../components/SuccessPopup"
import ErrorPopup from "../../../components/ErrorPopup"
import {
  updateCategory,
  resetCategoryState,
  fetchCategoryDetails,
} from "../../../redux/actions/event"

function EditCategory() {
  document.title = "Edit Category | Event | Admin Dashboard"

  const history = useNavigate()
  const dispatch = useDispatch()
  const { url } = useParams()

  const updateCategoryScreen = useSelector(
    state => state.screens.event.updateCategory
  )
  const fetchCategoryScreen = useSelector(
    state => state.screens.event.fetchCategoryDetails
  ) // TODO
  const eventState = useSelector(state => state.event) // TODO

  const [selectedIcon, setSelectedIcon] = useState([])
  const [iconUploading, setIconUploading] = useState(false)
  const [iconUploadError, setIconUploadError] = useState("")
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState("")
  const [successOpen, setSuccessOpen] = useState(false)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: "",
      description: "",
      tags: "",
      icon: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Please Enter Category Title"),
      // description: Yup.string().required("Please Enter Description"),
      // tags: Yup.string().required("Please Enter At Least One Tag"),
      // icon: Yup.string().required("Please Upload an Icon"),
    }),
    onSubmit: values => {
      const iconFilename =
        values.icon || (selectedIcon[0] && selectedIcon[0].serverFilename) || ""
      const categoryData = {
        title: values.title,
        description: values.description,
        tags: values.tags,
        icon: iconFilename,
        url, // include url from route param
      }
      dispatch(updateCategory(categoryData))
    },
  })

  // Tag logic
  const addTag = e => {
    if ((e.key === " " || e.key === "Enter") && tagInput.trim() !== "") {
      e.preventDefault()
      const newTag = tagInput.trim()
      if (!tags.includes(newTag)) {
        const newTags = [...tags, newTag]
        setTags(newTags)
        validation.setFieldValue("tags", newTags.join(", "))
      }
      setTagInput("")
    }
  }
  const removeTag = indexToRemove => {
    const newTags = tags.filter((_, index) => index !== indexToRemove)
    setTags(newTags)
    validation.setFieldValue("tags", newTags.join(", "))
  }
  const handleKeyDown = e => {
    if (e.key === "Backspace" && tagInput === "" && tags.length > 0) {
      removeTag(tags.length - 1)
    }
  }

  // Icon upload logic
  async function handleAcceptedIcon(files) {
    if (!files || files.length === 0) return
    setIconUploading(true)
    setIconUploadError("")
    const file = files[0]
    const formData = new FormData()
    formData.append("icon", file)
    fetch(`${process.env.REACT_APP_API_URL}/dashboard/upload-category-icon`, {
      method: "POST",
      body: formData,
    })
      .then(async response => {
        if (!response.ok) throw new Error("Icon upload failed")
        const data = await response.json()
        if (data.success && data.filename) {
          validation.setFieldValue("icon", data.filename)
          file.preview = URL.createObjectURL(file)
          file.formattedSize = formatBytes(file.size)
          file.serverFilename = data.filename
          setSelectedIcon([file])
        } else {
          throw new Error(data.message || "Icon upload failed")
        }
        setIconUploading(false)
      })
      .catch(err => {
        setIconUploading(false)
        setIconUploadError(err.message || "Icon upload failed")
        setSelectedIcon([])
      })
  }
  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }

  useEffect(() => {
    if (updateCategoryScreen.success) {
      setSuccessOpen(true)
      dispatch(resetCategoryState())
    } else if (updateCategoryScreen.error) {
      setErrorMsg(updateCategoryScreen.message)
      setErrorOpen(true)
      dispatch(resetCategoryState())
    }
  }, [updateCategoryScreen])

  // Fetch category data on mount using Redux dispatch
  useEffect(() => {
    if (!url) return
    dispatch(fetchCategoryDetails({ url }))
  }, [url])

  // Set form values from Redux state
  useEffect(() => {
    if (eventState && eventState.eventCategoryList) {
      const data = eventState.eventCategoryList
      validation.setValues({
        title: data.title || "",
        description: data.description || "",
        tags: data.tags || "",
        icon: data.icon || "",
      })
      setTags(
        (data.tags || "")
          .split(",")
          .map(t => t.trim())
          .filter(Boolean)
      )
    } else if (fetchCategoryScreen && fetchCategoryScreen.error) {
      setErrorMsg(
        fetchCategoryScreen.message || "Unable to fetch category data."
      )
      setErrorOpen(true)
    }
  }, [fetchCategoryScreen])

  useEffect(() => {
    if (validation.values.tags) {
      const initialTags = validation.values.tags
        .split(",")
        .map(tag => tag.trim())
        .filter(tag => tag !== "")
      setTags(initialTags)
    }
  }, [validation.values.tags])

  return (
    <React.Fragment>
      <SuccessPopup
        isOpen={successOpen}
        toggle={() => setSuccessOpen(false)}
        onConfirm={() => {
          setSuccessOpen(false)
          history("/events/category-list", { replace: true })
        }}
        message={"Category Updated Successfully!"}
      />
      <ErrorPopup
        isOpen={errorOpen}
        toggle={() => setErrorOpen(false)}
        message={errorMsg}
      />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            maintitle="Categories"
            title="Category"
            breadcrumbItem="Edit Category"
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form
                    className="mt-4"
                    onSubmit={e => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                    action="#"
                  >
                    <div className="row">
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="title">
                          Title
                        </Label>
                        <Input
                          name="title"
                          className="form-control"
                          placeholder="Enter Title"
                          type="text"
                          id="title"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.title || ""}
                          invalid={
                            validation.touched.title && validation.errors.title
                              ? true
                              : false
                          }
                        />
                        {validation.touched.title && validation.errors.title ? (
                          <FormFeedback type="invalid">
                            {validation.errors.title}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="tags">
                          Tags (Press space or enter to add a tag)
                        </Label>
                        <div
                          className="form-control"
                          style={{
                            minHeight: "42px",
                            padding: "0.25rem 0.5rem",
                          }}
                        >
                          <div className="d-flex flex-wrap align-items-center">
                            {tags.map((tag, index) => (
                              <div
                                key={index}
                                className="badge bg-primary me-1 mb-1 d-flex align-items-center"
                              >
                                {tag}
                                <button
                                  type="button"
                                  className="btn-close btn-close-white ms-1"
                                  style={{ fontSize: "0.5rem" }}
                                  onClick={() => removeTag(index)}
                                  aria-label="Remove tag"
                                />
                              </div>
                            ))}
                            <input
                              type="text"
                              className="border-0 flex-grow-1"
                              style={{ outline: "none", minWidth: "100px" }}
                              placeholder={
                                tags.length === 0
                                  ? "Type and press space or enter to add tags..."
                                  : ""
                              }
                              value={tagInput}
                              onChange={e => setTagInput(e.target.value)}
                              onKeyDown={addTag}
                              onBlur={validation.handleBlur}
                              id="tags"
                            />
                          </div>
                        </div>
                        <input
                          type="hidden"
                          name="tags"
                          value={validation.values.tags || ""}
                        />
                        {validation.touched.tags && validation.errors.tags ? (
                          <div className="invalid-feedback d-block">
                            {validation.errors.tags}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="row">
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="description">
                          Description
                        </Label>
                        <textarea
                          name="description"
                          id="description"
                          className={`form-control ${
                            validation.touched.description &&
                            validation.errors.description
                              ? "is-invalid"
                              : ""
                          }`}
                          rows="3"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.description || ""}
                        />
                        {validation.touched.description &&
                        validation.errors.description ? (
                          <FormFeedback type="invalid">
                            {validation.errors.description}
                          </FormFeedback>
                        ) : null}
                      </div>
                      {/*   <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="icon">
                          Category Icon
                        </Label>
                        <Dropzone
                          onDrop={acceptedFiles => {
                            handleAcceptedIcon(acceptedFiles)
                          }}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div className="dropzone">
                              <div
                                className="dz-message needsclick"
                                {...getRootProps()}
                              >
                                <input {...getInputProps()} />
                                <div className="mb-3">
                                  <i className="mdi mdi-cloud-upload display-4 text-muted"></i>
                                </div>
                                <h4>Drop icon here or click to upload.</h4>
                                <div className="text-muted mb-2" style={{ fontSize: '0.55em' }}>
                                  Icon size recommended: <b>128 × 128 px</b>
                                </div>
                                {iconUploading && (
                                  <div className="text-info mt-2" style={{ fontSize: '0.55em' }}>Uploading icon...</div>
                                )}
                                {iconUploadError && (
                                  <div className="text-danger mt-2" style={{ fontSize: '0.55em' }}>{iconUploadError}</div>
                                )}
                                {validation.values.icon && (
                                  <div className="text-success mt-2" style={{ fontSize: '0.55em' }}>Icon uploaded: {validation.values.icon}</div>
                                )}
                              </div>
                            </div>
                          )}
                        </Dropzone>
                        <div className="dropzone-previews mt-3" id="file-previews">
                          {selectedIcon.map((f, i) => (
                            <Card
                              className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                              key={i + "-icon"}
                            >
                              <div className="p-2">
                                <Row className="align-items-center">
                                  <Col className="col-auto">
                                    <img
                                      data-dz-thumbnail=""
                                      height="64"
                                      width="64"
                                      className="avatar-sm rounded bg-light"
                                      alt={f.name}
                                      src={f.preview}
                                    />
                                  </Col>
                                  <Col>
                                    <span className="text-muted font-weight-bold">
                                      {f.name}
                                    </span>
                                    <p className="mb-0">
                                      <strong>{f.formattedSize}</strong>
                                    </p>
                                  </Col>
                                  <Col className="col-auto">
                                    <Button color="danger" size="sm" onClick={() => {
                                      setSelectedIcon([]);
                                      validation.setFieldValue('icon', '');
                                    }}>
                                      Delete
                                    </Button>
                                  </Col>
                                </Row>
                              </div>
                            </Card>
                          ))}
                        </div>
                        <input type="hidden" name="icon" value={validation.values.icon || ''} />
                        {validation.touched.icon && validation.errors.icon ? (
                          <div className="invalid-feedback d-block">
                            {validation.errors.icon}
                          </div>
                        ) : null}
                      </div> */}
                    </div>
                    <div className="d-flex justify-content-center mt-3">
                      <Button
                        type="submit"
                        color="primary"
                        size="lg"
                        className="mt-3"
                      >
                        Submit
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default EditCategory
